import { Button, Label } from '@staff-app/ui';
import { HARDWARE_MAX_LINES, type HardwareCatalogProduct } from '../hooks/useBillingConsole';
import {
  optionKeyOf,
  type HardwareLineRow,
  type PrunedLines,
} from '../hooks/useDeviceLines';

interface DeviceLineRowsProps {
  /** Prefix for every testid, so two cards can render this without colliding. */
  keyId: string;
  rows: HardwareLineRow[];
  setRows: React.Dispatch<React.SetStateAction<HardwareLineRow[]>>;
  /** Products offerable for the current currency + region (from useDeviceLines). */
  options: HardwareCatalogProduct[];
  /** Every active product, offerable or not — used only for the "none offerable" notice. */
  allProducts: HardwareCatalogProduct[];
  currency: string;
  catalogLoading: boolean;
  pruned: PrunedLines | null;
  disabled?: boolean;
  /** Rendered as the section heading. Pass '' when the caller titles the section itself. */
  label?: string;
  /** Money formatter, passed in so this file owns no currency logic. */
  formatAmount: (minor: number, currency: string) => string;
  /**
   * The console's own field components, injected rather than imported: they live inside
   * BillingConsole.tsx, and importing them here would make that 4000-line module and this one
   * circular. Typed to the props actually used, not `any`.
   */
  LabeledSelect: React.ComponentType<{
    id: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    // Required, not optional: the real components require them, and a component that REQUIRES
    // a prop is not assignable to one whose prop is optional. This file always passes both.
    placeholder: string;
    options: { label: string; value: string }[];
    testId: string;
  }>;
  LabeledInput: React.ComponentType<{
    id: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    numeric?: boolean;
    testId: string;
  }>;
}

/**
 * The device picker: one row per line, product + quantity, add and remove.
 *
 * Presentational on purpose — every decision about WHICH products may be offered, what a
 * currency or region change invalidates, and what counts as a valid set of lines lives in
 * `useDeviceLines`. Shared by Create Hardware Only Invoice and by the onboarding card so those two
 * cannot drift on any of it; see that hook's docblock for why that matters.
 */
export function DeviceLineRows({
  keyId,
  rows,
  setRows,
  options,
  allProducts,
  currency,
  catalogLoading,
  pruned,
  disabled,
  label = 'Hardware',
  formatAmount,
  LabeledSelect,
  LabeledInput,
}: DeviceLineRowsProps) {
  const EMPTY = { optionKey: '', quantity: '1' };

  return (
    <>
      <div className="flex flex-col gap-2">
        {/* Omitted when the caller titles the section itself — otherwise the heading appears
            twice, which is what happened when this sat inside the onboarding card. */}
        {label ? <Label className="text-xs">{label}</Label> : null}
        {rows.map((row, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="flex-1">
              <LabeledSelect
                id={`${keyId}-sku-${i}`}
                label={i === 0 ? 'Product' : ''}
                value={row.optionKey}
                onChange={(v: string) =>
                  setRows((s) => s.map((r, j) => (j === i ? { ...r, optionKey: v } : r)))
                }
                placeholder={catalogLoading ? 'Loading…' : 'Select product'}
                options={options.map((p) => ({
                  label: `${p.name} — ${formatAmount(
                    p.prices.find((x) => x.currency === currency)?.amount ?? 0,
                    currency,
                  )}`,
                  value: optionKeyOf(p.sku, p.stripePriceId),
                }))}
                testId={`billing-${keyId}-sku-${i}`}
              />
            </div>
            <div className="w-24">
              <LabeledInput
                id={`${keyId}-qty-${i}`}
                label={i === 0 ? 'Qty' : ''}
                value={row.quantity}
                onChange={(v: string) =>
                  setRows((s) => s.map((r, j) => (j === i ? { ...r, quantity: v } : r)))
                }
                numeric
                testId={`billing-${keyId}-qty-${i}`}
              />
            </div>
            <Button
              onClick={() =>
                setRows((s) => (s.length === 1 ? [{ ...EMPTY }] : s.filter((_, j) => j !== i)))
              }
              disabled={disabled}
              variant="ghost"
              className="cursor-pointer"
              data-testid={`billing-${keyId}-remove-${i}`}
            >
              {'✕'}
            </Button>
          </div>
        ))}
        <div>
          <Button
            onClick={() => setRows((s) => [...s, { ...EMPTY }])}
            disabled={disabled || rows.length >= HARDWARE_MAX_LINES}
            variant="ghost"
            className="cursor-pointer"
            data-testid={`billing-${keyId}-add-line`}
          >
            {'+ Add line'}
          </Button>
        </div>
      </div>

      {/* Every active product exists but none can be offered here — almost always an unmapped
          catalog. Said explicitly, because an empty picker otherwise looks like a bug.

          Requires a CURRENCY, and that guard is load-bearing rather than cosmetic. With none
          chosen nothing can be priced, so this would fire on every product and blame the
          catalog for what is really "no plan picked yet" — sending an operator to Products List
          to fix something that is not broken. It also read as a broken sentence, naming a
          currency that is the empty string. The CALLER says what the real cause is. */}
      {!catalogLoading && !!currency && allProducts.length > 0 && options.length === 0 ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${keyId}-unmapped`}
        >
          {`The catalog has ${allProducts.length} active product${
            allProducts.length === 1 ? '' : 's'
          }, but none can be billed in ${currency.toUpperCase()} to this region — map them under Administration → Products List.`}
        </p>
      ) : null}

      {/* Lines a currency or region change invalidated. Naming them, with the reason, is the
          point: silently repricing or silently dropping is what we are avoiding. */}
      {pruned && pruned.names.length > 0 ? (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid={`billing-${keyId}-pruned`}
        >
          {`Removed ${pruned.names.join(', ')} — ${pruned.reason}.`}
        </p>
      ) : null}
    </>
  );
}
